import React from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * ProductSEO - Dynamic SEO metadata and JSON-LD structured data for Product Detail Page.
 * Uses real Firebase product data and conforms to Schema.org standards.
 */
function ProductSEO({ product, gender, subcategoryName, productId }) {
  if (!product) {
    return null;
  }

  const productName = (product.name || '').trim();
  const productCode = product.productCode ? String(product.productCode).trim() : '';
  const currentId = product.id || productId;

  // 1. Dynamic Title (~50-60 chars)
  const defaultSuffix = ' | DOR Dress On Rent';
  const shortSuffix = ' | DOR';
  const baseTitle = `${productName} on Rent`;

  let seoTitle = baseTitle + defaultSuffix;
  if (seoTitle.length > 60) {
    if ((baseTitle + shortSuffix).length <= 60) {
      seoTitle = baseTitle + shortSuffix;
    } else {
      const maxLen = 60 - shortSuffix.length - ' on Rent'.length;
      const truncatedName = productName.slice(0, Math.max(10, maxLen - 1)).trim();
      seoTitle = `${truncatedName}… on Rent${shortSuffix}`;
    }
  }

  // 2. Dynamic Meta Description (~140-160 chars)
  const genderLabel = gender === 'men' ? "men's " : gender === 'women' ? "women's " : '';
  const catLabel = subcategoryName ? `${subcategoryName} ` : '';
  let seoDescription = `Rent ${productName} from DOR Dress On Rent. Explore stylish ${genderLabel}${catLabel}outfits available for rental with flexible options.`;

  if (product.rent) {
    const rentNumber = Number(product.rent);
    if (!isNaN(rentNumber) && rentNumber > 0) {
      const rentText = ` Rent at ₹${rentNumber.toLocaleString('en-IN')}.`;
      if (seoDescription.length + rentText.length <= 160) {
        seoDescription += rentText;
      }
    }
  }

  if (Array.isArray(product.availableStores) && product.availableStores.length > 0) {
    const storeList = product.availableStores.slice(0, 2).join(', ');
    const storeText = ` Available in ${storeList}.`;
    if (seoDescription.length + storeText.length <= 160) {
      seoDescription += storeText;
    }
  }

  if (seoDescription.length > 160) {
    seoDescription = seoDescription.slice(0, 157).trim() + '...';
  }

  // 3. Canonical URL
  const encodedSubcategory = subcategoryName ? encodeURIComponent(subcategoryName) : '';
  const canonicalUrl = `https://www.dordressonrent.com/product/${gender || ''}/${encodedSubcategory}/${currentId}`;

  // 4. Image resolution (real images only)
  const images = [];
  const seen = new Set();

  if (product.imageUrl && typeof product.imageUrl === 'string' && product.imageUrl.startsWith('http')) {
    images.push(product.imageUrl);
    seen.add(product.imageUrl);
  }

  if (Array.isArray(product.images)) {
    product.images.forEach((img) => {
      if (img && typeof img === 'string' && img.startsWith('http') && !seen.has(img)) {
        images.push(img);
        seen.add(img);
      }
    });
  }

  const primaryImage = images[0] || (product.imageUrl && typeof product.imageUrl === 'string' ? product.imageUrl : '');
  const allImages = images;

  // 5. Schema.org Product Structured Data
  const productJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: productName,
    description: (product.description && product.description.trim()) || seoDescription,
    url: canonicalUrl,
  };

  if (allImages.length > 0) {
    productJsonLd.image = allImages;
  } else if (primaryImage) {
    productJsonLd.image = [primaryImage];
  }

  if (productCode) {
    productJsonLd.sku = productCode;
  }

  if (subcategoryName) {
    productJsonLd.category = subcategoryName;
  }

  if (product.color) {
    productJsonLd.color = product.color;
  }

  if (product.brand) {
    productJsonLd.brand = {
      '@type': 'Brand',
      name: product.brand,
    };
  }

  if (product.rent) {
    const rentNumber = Number(product.rent);
    if (!isNaN(rentNumber) && rentNumber > 0) {
      const offerSchema = {
        '@type': 'Offer',
        price: rentNumber,
        priceCurrency: 'INR',
        url: canonicalUrl,
      };

      // Add availability only if the product actually has listed stores or Pan India delivery
      if (Array.isArray(product.availableStores) && product.availableStores.length > 0) {
        offerSchema.availability = 'https://schema.org/InStock';
      }

      productJsonLd.offers = offerSchema;
    }
  }

  // 6. Schema.org BreadcrumbList Structured Data
  // Note: Since React Router does not have a dedicated /collection/:gender route,
  // the closest existing valid crawlable URL representing the hierarchy is the Home page.
  const subcategoryUrl = `https://www.dordressonrent.com/collection/${gender || ''}/${encodedSubcategory}`;
  const genderName = gender === 'men' ? 'Men' : gender === 'women' ? 'Women' : gender;

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: 'https://www.dordressonrent.com/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: genderName,
        item: 'https://www.dordressonrent.com/',
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: subcategoryName || 'Collection',
        item: subcategoryUrl,
      },
      {
        '@type': 'ListItem',
        position: 4,
        name: productName,
        item: canonicalUrl,
      },
    ],
  };

  return (
    <Helmet>
      {/* Primary Page Title & Description */}
      <title>{seoTitle}</title>
      <meta name="description" content={seoDescription} />

      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph Tags */}
      <meta property="og:type" content="product" />
      <meta property="og:title" content={seoTitle} />
      <meta property="og:description" content={seoDescription} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:site_name" content="DOR Dress On Rent" />
      {primaryImage && <meta property="og:image" content={primaryImage} />}

      {/* Twitter Card Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={seoTitle} />
      <meta name="twitter:description" content={seoDescription} />
      {primaryImage && <meta name="twitter:image" content={primaryImage} />}

      {/* Structured Data: Product */}
      <script type="application/ld+json">
        {JSON.stringify(productJsonLd)}
      </script>

      {/* Structured Data: BreadcrumbList */}
      <script type="application/ld+json">
        {JSON.stringify(breadcrumbJsonLd)}
      </script>
    </Helmet>
  );
}

export default ProductSEO;
